import React, { useEffect, useRef, useState } from 'react';
import './edit-player.styles.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import { useSelector } from 'react-redux';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { RandomString } from '@/utils/randomString';
import ProfileIcon from '@/pages/profile/profile-icon.component';
import useAnalytics from '@/utils/useAnalytics';
import { getOptimizedBlob } from '@/utils/getOptimizedBlob';

const EditPlayer = ({
  gameData,
  playerData,
  setMainAlert
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const membership = useSelector(state => state.userData.membership);
  const [formData, setFormData] = useState({});
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [enrollMethod, setEnrollMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passcodeFound, setPasscodeFound] = useState(false);
  const { trackEvent, trackException } = useAnalytics();
  const dataRef = useRef();
  const approveRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const storage = getStorage();

  useEffect(() => {
    if (!playerData || !userProfile) return;
    if (playerData.enroll_method === 'email') {
      setEnrollMethod('email');
      getEmailAddress();
    } else {
      getPasscode();
      setEnrollMethod('upload');
    }
    dataRef.current = { ...playerData };
    setFormData(dataRef.current)
  }, [playerData, userProfile])

  async function getPasscode() {
    if (location.pathname.includes('/preview/manager/')) {
      saveData('passcode', 'PreviewPasscode');
      setPasscodeFound(true);
      return;
    }
    // console.log(userProfile);
    const res = await UseCloudFunction(
      'getPlayerPasscode',
      {
        'userProfile': userProfile,
        'playerData': playerData
      }
    )
    // console.log(res);
    if (res.error) {
      setAlertMessage(`Sorry, we are unable to retrieve this user's passcode. Please try again later. (Error: ${res.error})`);
      return;
    }
    saveData('passcode', res.success);
    setPasscodeFound(true);
  }

  async function getEmailAddress() {
    if (location.pathname.includes('/preview/manager/')) {
      saveData('email', 'PreviewEmail');
      return;
    }
    const res = await UseCloudFunction(
      'getPlayerEmail',
      {
        'userProfile': userProfile,
        'playerData': playerData
      }
    )
    console.log(res)
    if (res.error) {
      setAlertMessage(`Sorry, we are unable to retrieve this user's email address. ` +
        `Please try again later. (Error: ${res.error})`);
      return;
    }
    saveData('email', res.success);
  }

  function saveData(key, value) {
    dataRef.current = { ...dataRef.current, ...{ [key]: value } }
    setFormData(dataRef.current);
    // console.log(dataRef.current);
  }

  function removePlayer() {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }
    const playerToRemove = { ...playerData }
    setConfirmMessage(
      playerToRemove.enroll_method === 'email' ?
        'Are you sure you want to remove this player? ' +
        'Because this user joined the game with an email-' +
        'based account, their account will be added to the ' +
        '"Banned User" list. You can allow this user to rejoin ' +
        'by going to the Game Settings and reactivating them ' +
        'from the Removed Player List.' :
        'Are you sure you want to remove this player? Because ' +
        'this user was uploaded, you can reinstate this user ' +
        'by going to Game Settings and reactivating them from ' +
        'the Removed Player List.'
    );
    approveRef.current = () => {
      continueRemoval();
    }

    async function continueRemoval() {

      setLoading(true);
      const res = await UseCloudFunction(
        'removePlayer',
        {
          'appId': userProfile.appId,
          'gameId': gameData.gameId,
          'playerData': playerData
        }
      )
      setLoading(false);
      console.log(res);
      if (res.error) {
        setAlertMessage(`Something went wrong. ` +
          `Please try again later. (Error: ${res.error})`);
        trackException(`Error removing player ${playerData.playerId} ` +
          `from game ${gameData.gameId}: ${res.error}`);
        return;
      }
      setMainAlert('Player removed successfully!');
      trackEvent(
        'player_removed_from_game',
        'Game Manager',
        `Player ${playerData.playerId} removed from game ${gameData.gameId}.`,
        null,
        membership
      )
      setTimeout(() => {
        navigate(`/manage/${gameData.path}/players`);
      }, 1000)
    }
  }

  function verifySave() {
    if (location.pathname.includes('/preview/manager/')) {
      setAlertMessage('Sorry! Data cannot be saved in Preview Mode.');
      return;
    }
    setConfirmMessage('Are you sure you want to save these changes?');
    approveRef.current = () => {
      setLoading(true);
      handleSubmit();
    }

  }

  async function handleSubmit() {
    if (!formData.icon) {
      saveProfile(null)
      return;
    }
    if (formData.icon.upload && formData.icon.type.indexOf('gif') == -1) {
      // formData.icon.croppie.result({
      //   type: 'blob',
      //   size: { 'width': 352, 'height': 352 },
      //   quality: 0.7,
      // })
      //   .then((blob) => {
      //     uploadImage(formData.icon, blob)
      //     return;
      //   })
      const optimizedBlob = await getOptimizedBlob(formData.icon.croppie, 20);
      uploadImage(formData.icon, optimizedBlob)
    } else if (formData.icon.upload) {
      uploadImage(formData.icon, formData.icon.file)
      return;
    } else if (formData.icon.url) {
      saveProfile(formData.icon.url)
      return;
    } else if (formData.icon) {
      saveProfile(formData.icon)
    } else {
      saveProfile();
    }
  }

  function uploadImage(profileIcon, blob) {
    const now = new Date().getTime();
    const path = `${userProfile.appId}/${now}-${formData.icon.name}`;
    const profileRef = ref(storage, path);
    const metadata = {
      contentType: formData.icon.type,
      customMetadata: {
        appId: playerData.appId
      }
    };
    uploadBytes(profileRef, blob, metadata).then((snapshot) => {
      getDownloadURL(profileRef)
        .then(async (url) => {
          saveProfile(url);
          const res = await UseCloudFunction(
            'saveToMyContent',
            {
              'gameId': gameData.gameId,
              'appId': userProfile.appId,
              'source': 'Player Icons',
              'category': 'Images',
              'cropped': true,
              'contentData': {
                'name': formData.icon.name,
                'url': url,
                'type': 'image',
                'id': `${now}_${RandomString(10)}_image`
              },
            }
          )
          if (res.error) {
            console.log('Error saving to My Content:', res.error);
            trackException(`Error saving player icon to My Content: ${res.error}`);
            return;
          }

          trackEvent(
            'player_icon_uploaded_to_my_content',
            'Game Manager',
            `Player ${playerData.playerId} uploaded new profile icon.`,
            null,
            membership
          )
          return;
        })
    })
  }

  const saveProfile = async (url) => {
    let tempData = { ...dataRef.current };
    const now = new Date().getTime();
    tempData.ts = now;
    if (url) {
      tempData.icon = url;
    }
    if (enrollMethod === 'email') {
      delete tempData.email;
    }

    const oldDisplayName = playerData.fName + ' ' +
      (playerData.lName ? playerData.lName[0] : '');
    const newDisplayName = tempData.fName + ' ' +
      (tempData.lName ? tempData.lName[0] : '');

    if (
      tempData.displayName === oldDisplayName
    ) {
      if (newDisplayName !== tempData.displayName) {
        tempData.displayName = newDisplayName;
      }
    }

    const res = await UseCloudFunction(
      'saveUploadedPlayerProfile',
      {
        'gameId': gameData.gameId,
        'profile': tempData,
        'appId': userProfile.appId
      }
    )
    if (res.error) {
      setAlertMessage(`Something went wrong. Try again later. (Error: ${res.error})`);
      trackException(`Error saving edited player profile for player ` +
        `${playerData.playerId}: ${res.error}`);
      return;
    }
    setAlertMessage('Player profile updated successfully.');
    trackEvent(
      'player_profile_edited',
      'Game Manager',
      `Player ${playerData.playerId} profile edited successfully.`,
      null,
      membership
    )
    setLoading(false);
    navigate(-1);
  }

  return (
    <div className='edit-player-container'>
      <div className='g-card'>
        <BackButton cancel={() => navigate(-1)} />
        <div className="g-space-1"></div>
        <div className='card-title'>Edit Player</div>
        <hr />
        <div className='g-double-col'>
          <div className='field small'>
            <label>First Name (required)</label>
            <input
              type='text'
              className={enrollMethod === 'email' ? 'disabled' : ''}
              value={formData.fName ?? ''}
              onChange={(e) => saveData('fName', e.target.value)}
            />
          </div>
          <div className='field small'>
            <label>Last Name, Initial, ID etc. (optional)</label>
            <input
              type='text'
              className={enrollMethod === 'email' ? 'disabled' : ''}
              value={formData.lName ?? ''}
              onChange={(e) => saveData('lName', e.target.value)}
            />
          </div>
        </div>
        <div className="g-space-1"></div>
        <div className='field small'>
          <label>Player Display Name (hidden last name)</label>
          <input
            type='text'
            value={formData.displayName ?? ''}
            onChange={(e) => saveData('displayName', e.target.value)}
          />
        </div>
        <div className="g-space-1"></div>
        <div className='g-double-col'>
          <div className='field small'>
            <label>Additional Information (optional)</label>
            <input
              type='text'
              value={formData.info ?? ''}
              onChange={(e) => saveData('info', e.target.value)} />
          </div>
          {
            (passcodeFound) &&
            <div className='field small'>
              <label>Passcode (required)</label>
              <input
                type='text'
                value={formData.passcode ?? ''}
                onChange={(e) => saveData('passcode', e.target.value)}
              />
            </div>
          }
          {
            (formData.email) &&
            <div className='field small'>
              <label>Email Address</label>
              <input
                type='text'
                className='disabled'
                defaultValue={formData.email ?? ''}
              />
            </div>
          }
        </div>
        <hr />
        {
          (enrollMethod === 'upload') &&
          <>
            <ProfileIcon
              formData={formData}
              saveData={saveData}
              ofAge={true}
              hideGoogleIcon={true} />
            <hr />
          </>
        }

        <div className='edit-buttons'>
          <button
            className='g-button med-btn red'
            onClick={() => removePlayer()}>
            Remove Player
            <FontAwesomeIcon icon={faTrash} />
          </button>
          <div className='buttons'>
            <button
              className='g-button'
              onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button
              className='g-button primary'
              onClick={() => verifySave()}>
              Save Changes
            </button>
          </div>
        </div>
      </div>
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      <FullPageLoader show={loading} />
    </div>
  )
}

export default EditPlayer